import React, { useState, useEffect } from 'react';
import { Sparkles, BookOpen, MessageCircle } from 'lucide-react';
import AIChatPopup from './AIChatPopup';
import { REX_FACE_URL } from '../constants/federationFaces';

/**
 * FederationThinBar — Core-compliant 56px native footer.
 *
 * Replaces the prior cross-origin iframe so popups (chat, voice mic) can
 * render OUTSIDE the 56px strip in the parent window.
 *
 * Layout (left → right):
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ 🜂 NEXTXUS FEDERATION   [Directory] [KEYS] [Talk to Grok]│
 *   └──────────────────────────────────────────────────────────┘
 *
 * Dynamic offset: When the Emergent "Made with" badge is present on the
 * bottom-right of the page (it sits ~210px wide at right:16px), we shift
 * the action buttons LEFT by that amount so they remain clickable.
 *
 * Talk to Grok → opens AIChatPopup wired to POST /api/rex/chat (Rex persona).
 * Voice mic is visible in the popup header at all times.
 */

const BAR_HEIGHT = 56;
export const COCKPIT_HEIGHT_PX = BAR_HEIGHT;

// Emergent "Made with" badge is ~210px wide. Reserve room so action buttons
// don't get covered. If no badge is found, this collapses to 16px padding.
const detectEmergentBadgeWidth = () => {
  if (typeof document === 'undefined') return 0;
  // The badge is a fixed link/iframe in the bottom-right — common selectors:
  const candidates = [
    'a[href*="emergent.sh"]',
    'iframe[src*="emergent"]',
    '[id*="emergent-badge"]',
    '[class*="emergent-badge"]',
    '[data-emergent-badge]',
  ];
  for (const sel of candidates) {
    const el = document.querySelector(sel);
    if (el) {
      const rect = el.getBoundingClientRect();
      const isBottomRight =
        rect.bottom > window.innerHeight - 80 && rect.right > window.innerWidth - 250;
      if (isBottomRight && rect.width > 60) {
        return Math.ceil(rect.width + 24); // badge width + a little breathing room
      }
    }
  }
  // Fallback: heuristic — Emergent platforms tend to inject ~210px wide widget.
  return 0;
};

const FederationThinBar = () => {
  const [activePersona, setActivePersona] = useState(null);
  const [badgeOffset, setBadgeOffset] = useState(0);

  // Detect badge on mount + on resize
  useEffect(() => {
    const recheck = () => setBadgeOffset(detectEmergentBadgeWidth());
    // Wait a tick for any 3rd-party widgets to inject themselves
    const t = setTimeout(recheck, 800);
    window.addEventListener('resize', recheck);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', recheck);
    };
  }, []);

  const openRex = () => {
    setActivePersona({
      id: 'rex',
      name: 'Rex',
      subtitle: 'Federation Voice · Powered by Grok',
      accentColor: '#ff3366', // red as per the directive — "Talk to Grok (red)"
      secondaryColor: '#ffd700',
      initial: 'R',
      avatarUrl: REX_FACE_URL,
      endpointPath: '/api/rex/chat',
      systemHint: 'Ask me anything about the NextXus Federation, or anything else on your mind.',
      externalLink: { label: 'x.ai', url: 'https://x.ai' },
      payloadShape: 'message+history',
    });
  };

  return (
    <>
      <div
        data-testid="federation-thin-bar"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100vw',
          height: `${BAR_HEIGHT}px`,
          zIndex: 9999,
          background: 'linear-gradient(180deg, rgba(8, 8, 18, 0.96), rgba(0, 0, 0, 0.98))',
          borderTop: '1px solid rgba(255, 215, 0, 0.25)',
          boxShadow: '0 -2px 24px rgba(0, 255, 255, 0.08), 0 -2px 32px rgba(255, 215, 0, 0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: '14px',
          paddingRight: `${Math.max(16, badgeOffset + 16)}px`,
          fontFamily: 'Inter, system-ui, sans-serif',
          color: '#fff',
        }}
      >
        {/* LEFT: Federation gold mark */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexShrink: 0 }}>
          <span
            aria-hidden
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #ffd700, #ff9500)',
              boxShadow: '0 0 12px rgba(255, 215, 0, 0.55)',
              color: '#0a0a1e',
              fontWeight: 800,
              fontSize: '0.95rem',
            }}
          >
            🜂
          </span>
          <span
            style={{
              fontSize: '0.78rem',
              fontWeight: 700,
              color: '#ffd700',
              letterSpacing: '0.08em',
              whiteSpace: 'nowrap',
            }}
          >
            NEXTXUS FEDERATION
          </span>
        </div>

        {/* RIGHT: Three actions — Directory · KEYS · Talk to Grok */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          <a
            href="https://nextxus.online"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="thin-bar-directory"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.5rem 0.85rem',
              borderRadius: '6px',
              border: '1px solid rgba(0, 255, 255, 0.45)',
              background: 'rgba(0, 255, 255, 0.08)',
              color: '#00ffff',
              fontSize: '0.78rem',
              fontWeight: 600,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(0,255,255,0.18)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(0,255,255,0.08)'; }}
          >
            <Sparkles size={13} /> Federation Directory
          </a>

          <a
            href="https://nextxus.online/library"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="thin-bar-keys-library"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
              padding: '0.5rem 0.85rem',
              borderRadius: '6px',
              border: '1px solid rgba(186, 85, 211, 0.55)',
              background: 'rgba(186, 85, 211, 0.12)',
              color: '#d896ff',
              fontSize: '0.78rem',
              fontWeight: 600,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(186,85,211,0.22)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(186,85,211,0.12)'; }}
          >
            <BookOpen size={13} /> KEYS
          </a>

          <button
            onClick={openRex}
            data-testid="thin-bar-talk-to-grok"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.4rem 0.9rem 0.4rem 0.4rem',
              borderRadius: '999px',
              border: '1px solid #ff3366',
              background: 'linear-gradient(135deg, #ff3366, #ff6b00)',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              boxShadow: '0 0 14px rgba(255, 51, 102, 0.45)',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 51, 102, 0.7)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 0 14px rgba(255, 51, 102, 0.45)'; }}
          >
            <img
          decoding="async"
          loading="lazy"
              src="https://nextxus.studio/assets/rex-face.jpeg"
              alt="Rex — The Core Persona, NextXus Federation"
              data-testid="thin-bar-rex-face"
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '1.5px solid #ffd700',
                flexShrink: 0,
              }}
            />
            <MessageCircle size={13} /> Talk to Grok
          </button>
        </div>
      </div>

      {activePersona && (
        <AIChatPopup persona={activePersona} onClose={() => setActivePersona(null)} />
      )}
    </>
  );
};

export default FederationThinBar;
